import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { api } from "../../config/api";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";

const styles = `
  .unread-notification {
    background-color: rgba(173, 216, 230, 0.5) !important;
  }
`;

const Notifications = ({ notifications, onClose, isLoading, type, user }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [readStatus, setReadStatus] = useState({});

  // Memoize the filtered notifications to prevent recalculation on each render
  const filteredNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) return [];

    // Split notification types into categories
    const regularTypes = [
      "new-inquiry",
      "warehouse-shipments",
      "quote-accepted",
      "updated-inquiry",
      "new-quote",
      "quotes-from-admin",
      "external-estimate-created",
      "estimate-generated",
      "estimation-accepted",
      "estimation-rejected",
      "invoice-generated",
      "todo-assigned",
      "quote-assigned",
      "task-completed",
      "shipment-status-update",
      "new-estimate",
      "update-estimate",
      "quote-cancelled",
      "shipments-update",
      "invoice-edited",
    ];

    if (type === "notifications") {
      return notifications.filter(notification => regularTypes.includes(notification.type));
    } else {
      // Chat notifications - filter out regular notifications and exclude messages sent by user
      return notifications.filter(notification =>
        (notification.type === "ticket-chat" || !regularTypes.includes(notification.type)) &&
        notification.sender !== user.uid
      );
    }
  }, [notifications, type, user?.uid]);


  // Derive notification link based on notification type and user role
  const getNotificationLink = (notification) => {
    const { type, link, _id } = notification;

    // Handle specific notification types
    switch (type) {
      case "warehouse-shipments":
        return "/shipments?filter=warehouse";
      case "est":
      case "new-quote":
      case "quotes-from-admin":
      case "external-estimate-created":
      case "shipments-update":
      case "new-estimate":
      case "estimate-generated":
      case "estimation-accepted":
      case "estimation-rejected":
        return link;
      case "quote-accepted":
        return "/shipments";
      case "quote-cancelled":
        return "/quoteadmin";
      case "updated-inquiry":
        return "/newRequest";
      case "invoice-generated":
        return link?.startsWith("/invoices")
          ? link
          : `/invoices?invoiceNo=${link?.split("invoiceNo=")[1]}`;
      case "todo-assigned":
      case "quote-assigned":
        return _id === user.uid ? link : link;
      case "task-completed":
        return user?.role === "super admin" ? link : link;
      case "shipment-status-update":
        if (user?.role === "user") {
          return "/myShipment";
        }
        return link;
      case "invoice-edited":
        return link;
      default:
        // Handle general cases
        if (user?.role !== "user") {
          return link;
        }

        if (link?.includes("/tickets")) {
          return link;
        } else if (link?.includes("/quoteadmin")) {
          return "/newquotes?" + link.slice("/quoteadmin/".length);
        } else {
          let pathPart = link?.slice(1) || "";
          let questionMarkIndex = pathPart.indexOf("?");
          if (
            questionMarkIndex !== -1 &&
            pathPart.charAt(questionMarkIndex - 1) === "s"
          ) {
            pathPart =
              pathPart.slice(0, questionMarkIndex - 1) +
              pathPart.slice(questionMarkIndex);
          }
          return "/my" + pathPart.charAt(0).toUpperCase() + pathPart.slice(1);
        }
    }
  };

  // Handle marking notification as read and navigation
  const handleMarkAsRead = async (id, link, type, role) => {
    try {
      // First update UI to reflect change immediately
      setReadStatus((prev) => ({ ...prev, [id]: true }));

      // Make API call to mark as read
      await api.put(`/notifications/${id}`);

      // Calculate the correct navigation link
      const newLink = getNotificationLink({ type, link, _id: id });

      // Update React Query cache to reflect the change
      queryClient.invalidateQueries(["notifications", user?.uid, user?.role]);

      // Navigate to the appropriate page
      navigate(newLink);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      // Revert UI change on error
      setReadStatus((prev) => ({ ...prev, [id]: false }));
    }
  };

  const unreadCount = filteredNotifications.filter(notification => !notification.read).length;

  return (
    <>
      <style>{styles}</style>
      <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
        <div className="p-4 border-b border-gray-300 shadow-md rounded-t-lg gradient-bg text-white">
          <h2 className="text-lg font-semibold">
            {type === "notifications" ? "Notifications" : "Chats"}
            {unreadCount > 0 && <span className="ml-2">({unreadCount})</span>}
          </h2>
          <button className="absolute top-3 right-3" onClick={onClose}>
            &#10005;
          </button>
        </div>
        <div className="h-96 overflow-y-auto pt-2">
          {filteredNotifications?.length > 0 ? (
            <ul className="notifications-list">
              {isLoading ? (
                <div>Loading...</div>
              ) : (
filteredNotifications.map((notification) => (
  <li
    key={notification?._id}
    className={`flex flex-col text-sm py-1 cursor-pointer p-4 ${
      !notification?.read && !readStatus[notification?._id]
        ? "unread-notification"
        : ""
    }`}
    onClick={() =>
      handleMarkAsRead(
        notification?._id,
        notification?.link,
        notification?.type,
        notification?.role
      )
    }
  >
    <strong>{notification.title}</strong>

    {notification?.quoteNumber ||
    notification?.inquiryNumber ||
    notification?.orderNumber ? (
      <span className="text-xs opacity-60">
        {notification.type === "new-inquiry" ||
        notification.type === "updated-inquiry"
          ? "Inquiry No: "
          : notification?.orderNumber
          ? "Order No: "
          : "Quote No: "}
        {notification?.orderNumber ||
          notification?.quoteNumber ||
          notification?.inquiryNumber}
      </span>
    ) : null}

    {notification.type === "ticket-chat" ? (
      <>
        <span className="text-sm mt-1">{notification.body}</span>
        {notification.ticketNumber && (
          <span className="text-xs opacity-60">
            Ticket #{notification.ticketNumber}
          </span>
        )}
      </>
    ) : (
      <span className="text-sm text-gray-600 mt-1">
        {notification.body}
      </span>
    )}

    <span className="text-xs opacity-60 w-full flex items-center justify-end mt-1">
      {dayjs(notification?.date).format("DD MMM YYYY hh:mm A")}
    </span>

    <hr className="mt-2" />
  </li>
))
              )}
            </ul>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-lg font-bold italic opacity-40">
                No notifications
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Notifications;
